// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useOtChainState } from '@react/redux/store'
import { PlanActionEnum } from '@shared/PlanActionCTO'

import Switch from '@mui/material/Switch'
import ButtonModal from '@react/components/ButtonModal'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import { planActionExecution } from '@react/lyt/planning/PlanActionExecution'
import React, { useState } from 'react'
import LytActionPlanModal from './LytActionPlanModal'
import { PlanActionClass } from './PlanActionClass'
import { ReservePlanModeEnum } from './ReserveActionPlanClass'

function getDisplayActionName(actionEnum: PlanActionEnum): string {
  switch (actionEnum) {
    case PlanActionEnum.RequestReserveRedeem:
      return 'Redeem from OT to Sierra Vault'
    case PlanActionEnum.ReserveNativeDeposit:
      return 'Make Deposit'
    case PlanActionEnum.TransferNativeForMarketMakerRedeem:
      return 'Transfer from Sierra Vault to Sierra Pool'
    default:
      return actionEnum
  }
}

export default function PlanActionCard(props: {
  planActionClass: PlanActionClass

  doRefresh: () => void
  doRedraw: () => void
}): React.ReactElement {
  const chainStateInitialized = useOtChainState()
  const {
    userConnection: { roleEnum, signer },
  } = chainStateInitialized

  const { planActionClass } = props

  const nativeLiquidityAsset = planActionClass.nativeLiquidityAsset
  const reservesWithStats = planActionClass.reserve
  const reserveActionPlanClass = planActionClass.reserveActionPlanClass

  const [actualAssets, setActualAssets] = useState<bigint>(planActionClass?.actualAssets ?? 0n)

  const [completed, setCompleted] = useState<boolean>(planActionClass?.completed ?? false)
  React.useEffect(() => {
    // subscribe on mount, unsubscribe on unmount
    return planActionClass.reserveActionPlanClass.subscribe(() => {
      logOT.info('PlanActionCard subscribed', planActionClass.reserveActionPlanClass.toString())
      setActualAssets(planActionClass?.actualAssets ?? 0n)
      setCompleted(planActionClass?.completed ?? false)
      logOT.info('setCompleted 2', planActionClass?.completed)
    })
  }, [planActionClass.reserveActionPlanClass])

  React.useEffect(() => {
    setActualAssets(planActionClass?.actualAssets ?? 0n)
    setCompleted(planActionClass?.completed ?? false)
    logOT.info('setCompleted 3', planActionClass?.completed)
    // subscribe on mount, unsubscribe on unmount
  }, [planActionClass])

  // Check if ProcessRedemptionForMarketMaker has sufficient funds
  const isInsufficientFunds =
    planActionClass.actionEnum === PlanActionEnum.ProcessRedemptionForMarketMaker &&
    nativeLiquidityAsset &&
    planActionClass.reserveActionPlanClass.nativeLiquidityAsset.lytPoolBalance < planActionClass.actualAssets

  const getDisableTooltip = () => {
    if (roleEnum !== planActionClass.roleEnum) {
      return `Action requires ${planActionClass.roleEnum} role`
    }
    if (isInsufficientFunds) {
      return `Insufficient funds in pool. Current balance: ` // ${erc20WeiToCommaDecString((planActionInteractive.liquidityAsset as LiquidityAssetWithBalanceCTO).lytPoolBalance)}, Required: ${erc20WeiToCommaDecString(planActionInteractive.assets)}`
    }
    return ''
  }

  const handleModalExecute = async (planActionClass: PlanActionClass) => {
    logOT.info('Executing plan action:', planActionClass.actionEnum)

    try {
      await planActionExecution(planActionClass, signer!)
      setCompleted(planActionClass?.completed ?? false)
      props.doRefresh()
      logOT.info('setCompleted 3', planActionClass?.completed)
      logOT.info('Plan action completed successfully, refreshing data')
    } catch (error) {
      logOT.error('Plan action failed:', error)
      throw error
    }
  }

  const handleModalCancel = () => {
    setActualAssets(planActionClass?.actualAssets ?? 0n)
    logOT.info('setCompleted 4', planActionClass?.completed)
  }

  return (
    <Box sx={{ marginBottom: 2 }}>
      <Paper sx={{ padding: 3, border: '1px solid', borderColor: 'grey.200' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 2,
            alignItems: 'center',
            marginBottom: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {getDisplayActionName(planActionClass.actionEnum)}
          </Typography>
          {reservesWithStats && (
            <Typography variant="body2" color="text.secondary">
              Pool: {reservesWithStats.poolName}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: planActionClass.actualShares === 0n ? '1fr auto' : '1fr 1fr auto',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 0.5 }}>
              Asset Amount
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {nativeLiquidityAsset?.name} {erc20WeiToCommaDecString(actualAssets)}
            </Typography>
          </Box>

          {planActionClass.actualShares !== 0n && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                Shares
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {erc20WeiToCommaDecString(planActionClass.actualShares)}
              </Typography>
            </Box>
          )}
          <Box>
            {reserveActionPlanClass.reservePlanMode !== ReservePlanModeEnum.OutflowMode &&
              reserveActionPlanClass.reservePlanMode !== ReservePlanModeEnum.NeutralMode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Adjust Mode</Typography>
                  <Switch
                    disabled={!reservesWithStats?.isAdjusting && reserveActionPlanClass.adjustingReserve2 !== undefined}
                    value={reservesWithStats?.isAdjusting ?? false}
                    // checked={reservesWithStats?.isAdjusting ?? false}
                    onChange={(event) => {
                      reserveActionPlanClass.switchIsAdjusting(event.target.checked, reservesWithStats?.id ?? '')
                      props.doRedraw()
                      if (reservesWithStats) {
                        //   reservesWithStats.isAdjusting = event.target.checked
                        //   reservesWithStats.notify()
                      }
                    }}
                  />
                </Box>
              )}
          </Box>
          {completed ? (
            <Box>Completed</Box>
          ) : planActionClass.actionEnum === PlanActionEnum.ReserveNativeDeposit ||
            planActionClass.actionEnum === PlanActionEnum.RequestReserveRedeem ||
            planActionClass.actionEnum === PlanActionEnum.TransferNativeForMarketMakerRedeem ||
            planActionClass.actionEnum === PlanActionEnum.ProcessRedemptionForMarketMaker ? (
            <ButtonModal
              title={getDisplayActionName(planActionClass.actionEnum)}
              disabled={roleEnum !== planActionClass.roleEnum || isInsufficientFunds}
              tooltip={getDisableTooltip()}
              modalCloseCallback={handleModalCancel}
              variant="primary"
              modalWidth={700}
            >
              <LytActionPlanModal
                planActionClass={planActionClass}
                onExecute={handleModalExecute}
                onCancel={handleModalCancel}
              />
            </ButtonModal>
          ) : (
            <Button
              variant="contained"
              disabled={roleEnum !== planActionClass.roleEnum}
              onClick={() => {
                handleModalExecute(planActionClass)
              }}
            >
              {getDisplayActionName(planActionClass.actionEnum)}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
